import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Play, Clock, User, Calendar, Award, X, Loader2, MessageSquare, Send, Trash2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to convert video URLs to embed URLs
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  }
  
  // If it's already an embed URL or unknown format, return as-is
  return url;
};

// Video Modal Component
function VideoModal({ isOpen, onClose, videoUrl, title }) {
  const embedUrl = getEmbedUrl(videoUrl);
  
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />
          
          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors z-10 flex items-center gap-2"
            >
              <span className="text-sm">Close</span>
              <X className="w-6 h-6" />
            </button>
            
            {/* Video Title */}
            <div className="absolute -top-12 left-0 text-white font-syne font-bold truncate max-w-[70%]">
              {title}
            </div>
            
            {/* Video Player */}
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title={title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <p>Video unavailable</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function FilmDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState({ isOpen: false, url: null, title: "" });
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(null);

  useEffect(() => {
    fetchFilm();
    fetchCommentsCount();
  }, [id]);

  const fetchFilm = async () => {
    try {
      const response = await axios.get(`${API}/films/${id}`);
      setFilm(response.data);
    } catch (error) {
      console.error("Error fetching film:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentsCount = async () => {
    try {
      const response = await axios.get(`${API}/films/${id}/comments/count`);
      setCommentsCount(response.data.count);
    } catch (error) {
      console.error("Error fetching comments count:", error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/films/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handleShowComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    if (!user) {
      toast.error("Please log in to comment");
      return;
    }

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/films/${id}/comments`, {
        content: newComment.trim()
      }, { withCredentials: true });
      
      toast.success("Comment added!");
      setNewComment("");
      fetchComments();
      fetchCommentsCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    
    setDeletingComment(commentId);
    try {
      await axios.delete(`${API}/films/${id}/comments/${commentId}`, { withCredentials: true });
      toast.success("Comment deleted");
      fetchComments();
      fetchCommentsCount();
    } catch (error) {
      toast.error("Failed to delete comment");
    } finally {
      setDeletingComment(null);
    }
  };

  const openVideoModal = (url, title) => {
    setVideoModal({ isOpen: true, url, title });
  };

  const closeVideoModal = () => {
    setVideoModal({ isOpen: false, url: null, title: "" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-purple animate-spin" />
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-slate-400 mb-4">Film not found</p>
        <Link to="/films" className="text-purple-light hover:text-white">
          Back to Films
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link 
            to="/films" 
            data-testid="back-to-films"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Films
          </Link>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Film Poster/Thumbnail */}
            <div className="md:col-span-1">
              <div className="aspect-video overflow-hidden bg-void-surface relative">
                <img 
                  src={film.poster_url || "https://images.unsplash.com/photo-1696479670605-aeeb206e1fcb?crop=entropy&cs=srgb&fm=jpg&q=85"}
                  alt={film.title}
                  className="w-full h-full object-cover"
                />
                {film.festival_winner && (
                  <div className="absolute top-3 left-3 px-3 py-1 bg-purple text-white text-xs uppercase tracking-wider font-mono flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    {film.festival_winner}
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="md:col-span-2">
              <span className="text-xs uppercase tracking-wider text-purple-light font-mono">
                {film.genre}
              </span>
              
              <h1 className="font-syne text-4xl md:text-5xl font-bold text-white mt-2 mb-6">
                {film.title}
              </h1>

              <div className="flex flex-wrap items-center gap-6 mb-8 text-slate-400">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {film.director}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {film.year}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {film.duration}
                </span>
              </div>

              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {film.description}
              </p>

              {film.awards && film.awards.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-syne font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-purple-light" />
                    Awards
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {film.awards.map((award, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-purple/20 text-purple-light text-sm"
                      >
                        {award}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4">
                {film.watch_url && (
                  <button
                    onClick={() => openVideoModal(film.watch_url, film.title)}
                    data-testid="watch-film"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
                  >
                    <Play className="w-4 h-4" />
                    Watch Film
                  </button>
                )}
                {film.trailer_url && (
                  <button
                    onClick={() => openVideoModal(film.trailer_url, `${film.title} - Trailer`)}
                    data-testid="watch-trailer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-bold uppercase tracking-widest text-xs rounded-full hover:border-white/50 transition-all"
                  >
                    <Play className="w-4 h-4" />
                    Trailer
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Comments Section - Hidden by default, but show prompt for logged-in users */}
      {(commentsCount > 0 || showComments || user) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
        >
          <div className="bg-void-surface border border-white/5 p-6">
            {/* Only show Comments header if there are comments or expanded */}
            {(commentsCount > 0 || showComments) && (
              <button
                onClick={handleShowComments}
                className="flex items-center gap-2 text-white font-syne font-bold mb-4 hover:text-purple-light transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
                Comments {commentsCount > 0 && `(${commentsCount})`}
              </button>
            )}

            <AnimatePresence>
              {showComments && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  {/* Add Comment Form */}
                  {user ? (
                    <div className="mb-6 pb-6 border-b border-white/5">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Share your thoughts about this film..."
                        rows={3}
                        className="bg-void border-white/10 text-white placeholder-slate-500 mb-3"
                        maxLength={500}
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">{newComment.length}/500</span>
                        <Button
                          onClick={handleSubmitComment}
                          disabled={submittingComment || !newComment.trim()}
                          className="bg-purple hover:bg-purple-hover text-white"
                        >
                          {submittingComment ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Post Comment
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 pb-6 border-b border-white/5">
                      <p className="text-slate-400 text-sm">
                        <Link to="/login" className="text-purple-light hover:underline">Log in</Link> to leave a comment.
                      </p>
                    </div>
                  )}

                  {/* Comments List */}
                  {comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-purple/20 flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-purple-light" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-white text-sm">{comment.user_name}</span>
                              <span className="text-xs text-slate-500">
                                {new Date(comment.created_at).toLocaleDateString()}
                              </span>
                              {user && (user.user_id === comment.user_id || ["admin", "super_admin"].includes(user.role)) && (
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  disabled={deletingComment === comment.id}
                                  className="ml-auto text-slate-500 hover:text-red-400 transition-colors"
                                >
                                  {deletingComment === comment.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                </button>
                              )}
                            </div>
                            <p className="text-slate-300 text-sm">{comment.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm text-center py-4">
                      Be the first to comment on this film!
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* "Be the first to comment" prompt when no comments and not expanded */}
            {commentsCount === 0 && !showComments && user && (
              <button
                onClick={handleShowComments}
                className="text-sm text-slate-400 hover:text-purple-light transition-colors"
              >
                Be the first to comment on this film
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Video Modal */}
      <VideoModal
        isOpen={videoModal.isOpen}
        onClose={closeVideoModal}
        videoUrl={videoModal.url}
        title={videoModal.title}
      />
    </div>
  );
}
